import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const navLink = 'relative px-4 py-2.5 text-sm font-medium rounded-full transition-colors';

export default function Navbar() {
  const { user, isAdmin, isWorker, signOut, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const handleLogoClick = () => {
    if (!user) {
      navigate('/');
      return;
    }

    if (isAdmin) {
      navigate('/admin');
    } else if (isWorker) {
      navigate('/worker');
    } else {
      navigate('/profile');
    }
  };

  const isPromotor = userProfile?.role === 'promotor';

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-40 border-b border-zinc-900/80 bg-black/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <button
            type="button"
            onClick={handleLogoClick}
            className="text-left hover:opacity-90 transition-opacity shrink-0"
          >
            {isPromotor ? (
              <span className="font-display font-black text-base text-white tracking-tight">
                F.A.C Autosport
              </span>
            ) : (
              <>
                <span className="font-display font-black text-sm sm:text-base text-white tracking-tight block leading-none">
                  JACKY CIRCUIT
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500 mt-1 block">
                  Drag · Drift
                </span>
              </>
            )}
          </button>

          <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
            {user ? (
              <>
                {isAdmin ? (
                  <Link
                    to="/admin"
                    className={`${navLink} ${isActive('/admin') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                  >
                    Admin
                    {isActive('/admin') && (
                      <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                    )}
                  </Link>
                ) : isWorker ? (
                  <Link
                    to="/worker"
                    className={`${navLink} ${isActive('/worker') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                  >
                    Dashboard
                    {isActive('/worker') && (
                      <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                    )}
                  </Link>
                ) : (
                  <>
                    {isPromotor && (
                      <Link
                        to="/services"
                        className={`${navLink} ${isActive('/services') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                      >
                        Booking
                        {isActive('/services') && (
                          <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                        )}
                      </Link>
                    )}
                    <Link
                      to="/events"
                      className={`${navLink} ${isActive('/events') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Events
                      {isActive('/events') && (
                        <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                      )}
                    </Link>
                    <Link
                      to="/gallery"
                      className={`${navLink} ${isActive('/gallery') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Gallery
                      {isActive('/gallery') && (
                        <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                      )}
                    </Link>
                    <Link
                      to="/tournament-results"
                      className={`${navLink} hidden sm:inline-flex ${isActive('/tournament-results') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Results
                      {isActive('/tournament-results') && (
                        <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                      )}
                    </Link>
                    <Link
                      to="/profile"
                      className={`${navLink} ${isActive('/profile') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Profile
                      {isActive('/profile') && (
                        <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                      )}
                    </Link>
                  </>
                )}
                <button type="button" onClick={handleSignOut} className="btn-primary text-sm py-2 px-4">
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/gallery"
                  className={`${navLink} ${isActive('/gallery') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                >
                  Gallery
                  {isActive('/gallery') && (
                    <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                  )}
                </Link>
                <Link
                  to="/tournament-results"
                  className={`${navLink} hidden sm:inline-flex ${isActive('/tournament-results') ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                >
                  Leaderboard
                  {isActive('/tournament-results') && (
                    <span className="absolute left-1/2 bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                  )}
                </Link>
                <Link to="/login" className={`${navLink} text-zinc-400 hover:text-white`}>
                  Login
                </Link>
                <Link to="/register" className="btn-primary text-sm py-2 px-4">
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
